import { useThrottledValue } from '@mantine/hooks';
import cx from 'clsx';
import { motion } from 'motion/react';
import type { ReactNode } from 'react';

import { ErrorBoundaryWrapper } from '#core/components/error/ErrorBoundaryWrapper.tsx';

import classes from './DashboardGrid.module.css';
import { DashboardGridPropsSchema } from './dashboard-grid.utils.ts';
import { GridItemRenderer } from './GridItemRenderer.tsx';
import { containerTransition } from './panels/shared/constants.ts';
import type { DashboardGridData, DashboardGridItem } from './reconciler/serialize.ts';

/**
 * Type for renderItem content callback.
 *
 * When provided, DashboardGrid handles the cell wrapper (positioning, caption, layer styling)
 * and calls this function to render the inner content.
 *
 * @param item - The grid item data
 * @param index - The item's index in the items array (enables streaming detection)
 * @param totalItems - Total number of items (for streaming detection)
 * @returns ReactNode to render inside the grid cell
 */
export type RenderItemContentFn = (item: DashboardGridItem, index: number, totalItems: number) => ReactNode;

/**
 * Props for DashboardGrid component.
 */
export interface DashboardGridProps {
  /** JSON data for the dashboard grid. Takes precedence over children if both provided. */
  data?: DashboardGridData;
  /** JSX children using dashboard-grid elements (uses reconciler). Ignored if data is provided. */
  children?: ReactNode;
  /** Number of grid rows. Enables flattened JSX syntax when provided with children. */
  rows?: number;
  /** Number of grid columns. Enables flattened JSX syntax when provided with children. */
  columns?: number;
  /** Dashboard title. Used with flattened JSX syntax. */
  title?: string;
  /**
   * Custom content renderer for advanced use cases (e.g., data adapters, streaming).
   * When provided, DashboardGrid handles the cell wrapper (positioning, caption, layer, error boundary)
   * and calls this function to render the inner content.
   *
   * The index and totalItems enable streaming detection: the last item (index === totalItems - 1)
   * may be streaming when the LLM is still generating.
   */
  renderItemContent?: RenderItemContentFn;
}

/**
 * Unified DashboardGrid component supporting JSON and JSX APIs.
 */
export function DashboardGrid(props: DashboardGridProps) {
  return (
    <ErrorBoundaryWrapper context="DashboardGrid" showRetry title="Dashboard failed to load" variant="alert">
      <DashboardGridContent {...useThrottledValue(props, 500)} />
    </ErrorBoundaryWrapper>
  );
}

function DashboardGridContent({ renderItemContent, ...props }: DashboardGridProps) {
  const { layout, items, title: gridTitle } = DashboardGridPropsSchema.parse(props);

  const isSingleItem = layout.rows === 1 && layout.columns === 1 && items.length === 1;
  const totalItems = items.length;

  return (
    <div className={cx(classes.gridWrapper, isSingleItem && classes.singleComponent)}>
      {gridTitle && <div className={classes.gridTitle}>{gridTitle}</div>}
      <motion.div
        animate="show"
        className={classes.gridContainer}
        data-testid="dashboard-grid"
        exit="exit"
        initial="hidden"
        role="grid"
        variants={containerTransition}
      >
        {items.map((item, index) => {
          const itemKey = `grid-${index}-${item.position.row}-${item.position.column}`;

          if (renderItemContent) {
            const customContent = renderItemContent(item, index, totalItems);
            return <GridItemRenderer item={item} key={itemKey} renderContent={customContent} />;
          }

          return <GridItemRenderer item={item} key={itemKey} />;
        })}
      </motion.div>
    </div>
  );
}
